import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Brush,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  PolarAngleAxis,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  FiAlertTriangle,
  FiArrowDownRight,
  FiArrowUpRight,
  FiBarChart2,
  FiChevronRight,
  FiClock,
  FiCreditCard,
  FiFileText,
  FiMapPin,
  FiPackage,
  FiPlus,
  FiRefreshCw,
  FiRepeat,
  FiRotateCcw,
  FiShoppingBag,
  FiShoppingCart,
  FiTarget,
  FiTrendingUp,
  FiUsers,
} from "react-icons/fi";

import api from "../api/axios";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
// dashboard.css is still loaded because other pages (ProfileDash, the session
// loader, …) rely on classes it defines.
import "../assets/styles/dashboard.css";
import "../assets/styles/analytics-dashboard.css";

const PAGE_NAME = "Dashboard";

const RANGES = [
  { value: "7d", label: "7D", days: 7, title: "Last 7 days" },
  { value: "30d", label: "30D", days: 30, title: "Last 30 days" },
  { value: "90d", label: "90D", days: 90, title: "Last 90 days" },
  { value: "1y", label: "12M", days: 365, title: "Last 12 months" },
];

const SALES_METRICS = [
  { value: "revenue", label: "Revenue" },
  { value: "orders", label: "Orders" },
  { value: "aov", label: "Avg. order" },
];

// Fixed semantic colours, chosen to stay readable on light and dark cards.
const STATUS_META = {
  pending: { label: "Pending", color: "#94a3b8" },
  confirmed: { label: "Confirmed", color: "#3b82f6" },
  processing: { label: "Processing", color: "#f59e0b" },
  in_production: { label: "In production", color: "#f97316" },
  shipped: { label: "Shipped", color: "#8b5cf6" },
  delivered: { label: "Delivered", color: "#10b981" },
  cancelled: { label: "Cancelled", color: "#ef4444" },
  returned: { label: "Returned", color: "#ec4899" },
  refunded: { label: "Refunded", color: "#e879f9" },
};

const SERIES_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444", "#14b8a6"];

const getPermissionHeaders = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const showRequestError = (error, fallbackMessage) => {
  // The Axios interceptor handles the global 403 popup.
  if (error.response?.status === 403) return;

  toast.error(error.response?.data?.message || fallbackMessage);
};

/* ---------- Formatting ---------- */

const formatMoney = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  })}`;

const compactNumber = new Intl.NumberFormat("en-IN", {
  notation: "compact",
  maximumFractionDigits: 1,
});

const formatCompactMoney = (value) => `₹${compactNumber.format(Number(value || 0))}`;

const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;

const toDateKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;

const toMonthKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const normaliseDateKey = (value) => {
  if (!value) return "";
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? "" : toDateKey(parsed);
};

const timeAgo = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const seconds = Math.round((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;

  return date.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const parseJson = (value) => {
  if (!value) return {};
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

const getStatusMeta = (status) =>
  STATUS_META[status] || {
    label: String(status || "Unknown").replace(/_/g, " "),
    color: "#64748b",
  };

/* ---------- Series builders (fill gaps so charts show quiet days) ---------- */

const buildDailySeries = (rows, days) => {
  const byDay = new Map(rows.map((row) => [normaliseDateKey(row.date), row]));
  const today = new Date();
  const series = [];

  for (let offset = days - 1; offset >= 0; offset -= 1) {
    const date = new Date(today);
    date.setDate(today.getDate() - offset);

    const row = byDay.get(toDateKey(date));
    const revenue = Number(row?.revenue || 0);
    const orders = Number(row?.orders || 0);

    series.push({
      key: toDateKey(date),
      label: date.toLocaleDateString("en-IN", { day: "numeric", month: "short" }),
      fullLabel: date.toLocaleDateString("en-IN", {
        weekday: "short",
        day: "numeric",
        month: "short",
      }),
      revenue,
      orders,
      aov: orders > 0 ? revenue / orders : 0,
    });
  }

  return series;
};

const buildMonthlySeries = (rows) => {
  const byMonth = new Map(rows.map((row) => [String(row.month), row]));
  const today = new Date();
  const series = [];

  for (let offset = 11; offset >= 0; offset -= 1) {
    const date = new Date(today.getFullYear(), today.getMonth() - offset, 1);
    const row = byMonth.get(toMonthKey(date));
    const revenue = Number(row?.revenue || 0);
    const orders = Number(row?.orders || 0);

    series.push({
      key: toMonthKey(date),
      label: date.toLocaleDateString("en-IN", { month: "short" }),
      fullLabel: date.toLocaleDateString("en-IN", { month: "long", year: "numeric" }),
      revenue,
      orders,
      aov: orders > 0 ? revenue / orders : 0,
    });
  }

  return series;
};

/* ---------- Hooks ---------- */

const readPalette = () => {
  const styles = getComputedStyle(document.documentElement);
  const read = (name, fallback) => styles.getPropertyValue(name).trim() || fallback;

  return {
    accent: read("--accent", "#9a6a32"),
    text: read("--text", "#24160d"),
    strong: read("--text-strong", "#100905"),
    muted: read("--muted", "#8a7158"),
    border: read("--border", "#e8d7c0"),
    card: read("--card", "#ffffff"),
  };
};

/**
 * Chart libraries need real colour values, not CSS variables. The theme
 * provider writes its variables in an effect, so read them on the next frame
 * after any theme change.
 */
const useChartPalette = () => {
  const themeContext = useTheme();
  const theme = themeContext?.theme;
  const [palette, setPalette] = useState(readPalette);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setPalette(readPalette()));
    return () => cancelAnimationFrame(frame);
  }, [theme]);

  return palette;
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const useCountUp = (value, duration = 900) => {
  const target = Number(value) || 0;
  const [display, setDisplay] = useState(0);
  const currentRef = useRef(0);

  useEffect(() => {
    if (prefersReducedMotion()) {
      currentRef.current = target;
      setDisplay(target);
      return undefined;
    }

    const from = currentRef.current;
    const startedAt = performance.now();
    let frame;

    const tick = (now) => {
      const progress = Math.min((now - startedAt) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const next = from + (target - from) * eased;

      currentRef.current = next;
      setDisplay(next);

      if (progress < 1) frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, duration]);

  return display;
};

/* ---------- Small components ---------- */

const CountUp = ({ value, format = (number) => Math.round(number).toLocaleString("en-IN") }) => {
  const display = useCountUp(value);
  return <>{format(display)}</>;
};

const Delta = ({ value, suffix = "vs previous period" }) => {
  const number = Number(value || 0);
  const tone = number > 0 ? "up" : number < 0 ? "down" : "flat";

  return (
    <span className={`adb-delta is-${tone}`}>
      {number > 0 ? <FiArrowUpRight /> : number < 0 ? <FiArrowDownRight /> : null}
      {number > 0 ? "+" : ""}
      {number.toFixed(1)}%<small>{suffix}</small>
    </span>
  );
};

const ChartTooltip = ({ active, payload, label, formatters = {}, labelFormatter }) => {
  if (!active || !payload?.length) return null;

  const heading = labelFormatter ? labelFormatter(label, payload) : label;

  return (
    <div className="adb-tooltip">
      {heading && <strong>{heading}</strong>}
      {payload.map((entry) => {
        const format = formatters[entry.dataKey] || ((number) => number);
        return (
          <div key={entry.dataKey || entry.name}>
            <i style={{ background: entry.color || entry.payload?.fill }} />
            <span>{entry.name}</span>
            <b>{format(entry.value)}</b>
          </div>
        );
      })}
    </div>
  );
};

const Panel = ({ title, subtitle, icon, actions, className = "", children, onOpen, openLabel }) => (
  <section className={`adb-panel ${className}`}>
    <header className="adb-panel-head">
      <div className="adb-panel-title">
        {icon && <span className="adb-panel-icon">{icon}</span>}
        <div>
          <h3>{title}</h3>
          {subtitle && <p>{subtitle}</p>}
        </div>
      </div>

      <div className="adb-panel-actions">
        {actions}
        {onOpen && (
          <button type="button" className="adb-link" onClick={onOpen}>
            {openLabel || "View all"} <FiChevronRight />
          </button>
        )}
      </div>
    </header>

    {children}
  </section>
);

const Segmented = ({ options, value, onChange, label }) => (
  <div className="adb-segmented" role="tablist" aria-label={label}>
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        role="tab"
        aria-selected={value === option.value}
        className={value === option.value ? "is-active" : ""}
        onClick={() => onChange(option.value)}
        title={option.title}
      >
        {option.label}
      </button>
    ))}
  </div>
);

const EmptyChart = ({ icon, text }) => (
  <div className="adb-empty">
    {icon}
    <span>{text}</span>
  </div>
);

const Sparkline = ({ data, dataKey, color, id }) => (
  <div className="adb-spark" aria-hidden="true">
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.35} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area
          type="monotone"
          dataKey={dataKey}
          stroke={color}
          strokeWidth={2}
          fill={`url(#${id})`}
          isAnimationActive={!prefersReducedMotion()}
        />
      </AreaChart>
    </ResponsiveContainer>
  </div>
);

const KpiCard = ({ icon, label, value, format, delta, spark, onClick, tone }) => {
  const Component = onClick ? "button" : "div";

  return (
    <Component
      type={onClick ? "button" : undefined}
      className={`adb-kpi ${onClick ? "is-clickable" : ""}`}
      onClick={onClick}
    >
      <div className="adb-kpi-top">
        <span className="adb-kpi-icon" style={tone ? { color: tone } : undefined}>
          {icon}
        </span>
        <span className="adb-kpi-label">{label}</span>
      </div>

      <strong className="adb-kpi-value">
        <CountUp value={value} format={format} />
      </strong>

      <div className="adb-kpi-foot">
        <Delta value={delta} />
      </div>

      {spark}
    </Component>
  );
};

const SkeletonDashboard = () => (
  <div className="adb-grid" aria-busy="true" aria-label="Loading dashboard">
    {[3, 3, 3, 3, 8, 4, 7, 5].map((span, index) => (
      <div key={index} className={`adb-panel adb-skeleton adb-span-${span}`}>
        <span />
        <span />
        <span />
      </div>
    ))}
  </div>
);

/* ---------- Page ---------- */

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const palette = useChartPalette();

  const [dashboard, setDashboard] = useState(null);
  const [userStats, setUserStats] = useState({});
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [range, setRange] = useState("30d");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [salesMetric, setSalesMetric] = useState("revenue");
  const [productMetric, setProductMetric] = useState("revenue");
  const [activeStatus, setActiveStatus] = useState(null);
  const [activePayment, setActivePayment] = useState(null);

  const fetchDashboard = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (silent) setRefreshing(true);
        else setLoading(true);

        const [dashboardRes, userStatsRes, recentCustomersRes] = await Promise.all([
          api.get("/admin_dashboard", {
            headers: getPermissionHeaders("view"),
            params: { range },
          }),
          api.get("/admin_dashboard_users/stats", {
            headers: getPermissionHeaders("view"),
          }),
          api.get("/admin_dashboard_users/recent-customers", {
            headers: getPermissionHeaders("view"),
          }),
        ]);

        setDashboard(dashboardRes.data?.data || {});
        setUserStats(userStatsRes.data?.data || {});
        setRecentCustomers(recentCustomersRes.data?.data || []);
        setLastUpdated(new Date());
      } catch (error) {
        showRequestError(error, "Failed to fetch dashboard data.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [range]
  );

  useEffect(() => {
    // Keep the current charts on screen while a new range loads.
    fetchDashboard({ silent: Boolean(dashboard) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const rangeMeta = RANGES.find((item) => item.value === range) || RANGES[1];

  const summary = dashboard?.summary || {};
  const insights = dashboard?.business_insights || {};
  const period = insights.period || {};
  const growth = insights.growth || {};
  const disputes = insights.disputes || {};
  const customers = insights.customers || {};
  const fulfilment = insights.fulfilment || {};
  const cartInsights = dashboard?.cart_insights || {};

  const salesSeries = useMemo(() => {
    if (!dashboard) return [];
    return range === "1y"
      ? buildMonthlySeries(dashboard.revenue_by_month || [])
      : buildDailySeries(dashboard.sales_overview || [], rangeMeta.days);
  }, [dashboard, range, rangeMeta.days]);

  const salesStats = useMemo(() => {
    const total = salesSeries.reduce((sum, row) => sum + row[salesMetric === "aov" ? "revenue" : salesMetric], 0);
    const orders = salesSeries.reduce((sum, row) => sum + row.orders, 0);
    const best = salesSeries.reduce(
      (top, row) => (row[salesMetric] > (top?.[salesMetric] ?? -1) ? row : top),
      null
    );
    const activeDays = salesSeries.filter((row) => row.orders > 0).length;

    return {
      total: salesMetric === "aov" ? (orders > 0 ? total / orders : 0) : total,
      best,
      average: salesSeries.length ? total / salesSeries.length : 0,
      activeDays,
    };
  }, [salesSeries, salesMetric]);

  const statusData = useMemo(
    () =>
      (dashboard?.order_status || [])
        .filter((row) => Number(row.count) > 0)
        .map((row) => ({
          status: row.status,
          name: getStatusMeta(row.status).label,
          value: Number(row.count || 0),
          color: getStatusMeta(row.status).color,
        }))
        .sort((a, b) => b.value - a.value),
    [dashboard]
  );

  const statusTotal = statusData.reduce((sum, row) => sum + row.value, 0);

  const paymentData = useMemo(
    () =>
      (dashboard?.payment_methods || [])
        .map((row, index) => ({
          name: String(row.method || "Unknown").replace(/_/g, " ").toUpperCase(),
          value: Number(row.amount || 0),
          count: Number(row.count || 0),
          color: index === 0 ? palette.accent : SERIES_COLORS[(index - 1) % SERIES_COLORS.length],
        }))
        .sort((a, b) => b.value - a.value),
    [dashboard, palette.accent]
  );

  const paymentTotal = paymentData.reduce((sum, row) => sum + row.value, 0);

  const productData = useMemo(
    () =>
      (dashboard?.top_products || []).slice(0, 7).map((product) => ({
        id: product.product_id,
        name: product.name || "Product",
        revenue: Number(product.revenue || 0),
        quantity: Number(product.quantity || 0),
        orders: Number(product.orders || 0),
        image: product.image,
      })),
    [dashboard]
  );

  const sortedProducts = useMemo(
    () => [...productData].sort((a, b) => b[productMetric] - a[productMetric]),
    [productData, productMetric]
  );

  const locations = insights.top_locations || [];
  const maxLocationRevenue = Math.max(...locations.map((row) => Number(row.revenue || 0)), 1);

  const funnelSteps = [
    { label: "Carts started", value: Number(period.carts_started || 0), icon: <FiShoppingCart /> },
    { label: "Checked out", value: Number(period.carts_converted || 0), icon: <FiCreditCard /> },
    { label: "Paid orders", value: Number(period.paid_orders || 0), icon: <FiShoppingBag /> },
  ];
  const funnelMax = Math.max(funnelSteps[0].value, 1);

  const repeatRate = Number(customers.repeat_rate || 0);

  const recentOrders = (dashboard?.recent_orders || []).slice(0, 6);

  const greeting = (() => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  })();

  const axisTick = { fill: palette.muted, fontSize: 11 };
  const reduceMotion = prefersReducedMotion();

  const salesFormatter =
    salesMetric === "orders"
      ? (value) => Math.round(value).toLocaleString("en-IN")
      : formatMoney;

  const salesMetricLabel = SALES_METRICS.find((metric) => metric.value === salesMetric)?.label;

  if (loading && !dashboard) {
    return (
      <div className="adb-page">
        <div className="adb-hero">
          <div>
            <p>{greeting}</p>
            <h1>{user?.name || "Admin"}</h1>
            <span>Preparing your store analytics…</span>
          </div>
        </div>
        <SkeletonDashboard />
      </div>
    );
  }

  return (
    <div className={`adb-page ${refreshing ? "is-refreshing" : ""}`}>
      {/* ---------- Header ---------- */}
      <div className="adb-hero">
        <div>
          <p>{greeting}</p>
          <h1>{user?.name || "Admin"}</h1>
          <span>
            Here's how your store is doing · {rangeMeta.title.toLowerCase()}
          </span>
        </div>

        <div className="adb-hero-actions">
          <Segmented
            label="Reporting period"
            options={RANGES}
            value={range}
            onChange={setRange}
          />

          <button
            type="button"
            className="adb-icon-button"
            onClick={() => fetchDashboard({ silent: true })}
            disabled={refreshing}
            title={lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString("en-IN")}` : "Refresh"}
            aria-label="Refresh dashboard"
          >
            <FiRefreshCw className={refreshing ? "adb-spin" : ""} />
          </button>

          <button
            type="button"
            className="primary-action adb-primary"
            onClick={() => navigate("/products/add")}
          >
            <FiPlus /> Add product
          </button>
        </div>
      </div>

      <div className="adb-grid">
        {/* ---------- KPIs ---------- */}
        <div className="adb-span-3">
          <KpiCard
            icon={<FiTrendingUp />}
            label="Revenue"
            value={period.revenue ?? summary.total_revenue}
            format={formatMoney}
            delta={growth.revenue}
            onClick={() => navigate("/invoices")}
            spark={
              <Sparkline data={salesSeries} dataKey="revenue" color={palette.accent} id="spark-revenue" />
            }
          />
        </div>

        <div className="adb-span-3">
          <KpiCard
            icon={<FiShoppingBag />}
            label="Orders"
            value={summary.total_orders}
            delta={growth.orders}
            tone="#3b82f6"
            onClick={() => navigate("/orders")}
            spark={<Sparkline data={salesSeries} dataKey="orders" color="#3b82f6" id="spark-orders" />}
          />
        </div>

        <div className="adb-span-3">
          <KpiCard
            icon={<FiBarChart2 />}
            label="Average order value"
            value={period.average_order_value}
            format={formatMoney}
            delta={growth.average_order_value}
            tone="#10b981"
            spark={<Sparkline data={salesSeries} dataKey="aov" color="#10b981" id="spark-aov" />}
          />
        </div>

        <div className="adb-span-3">
          <KpiCard
            icon={<FiTarget />}
            label="Cart conversion"
            value={period.conversion_rate}
            format={formatPercent}
            delta={growth.conversion_rate}
            tone="#f59e0b"
            spark={
              <div className="adb-kpi-meter" aria-hidden="true">
                <span style={{ width: `${Math.min(Number(period.conversion_rate || 0), 100)}%` }} />
              </div>
            }
          />
        </div>

        {/* ---------- Sales performance ---------- */}
        <Panel
          className="adb-span-8"
          title="Sales performance"
          subtitle={`${range === "1y" ? "Monthly" : "Daily"} paid sales · drag the handles below to zoom`}
          icon={<FiTrendingUp />}
          actions={
            <Segmented
              label="Sales metric"
              options={SALES_METRICS}
              value={salesMetric}
              onChange={setSalesMetric}
            />
          }
        >
          <div className="adb-stat-strip">
            <div>
              <span>{salesMetric === "aov" ? "Average order" : `Total ${salesMetricLabel.toLowerCase()}`}</span>
              <strong>
                <CountUp value={salesStats.total} format={salesFormatter} />
              </strong>
            </div>
            <div>
              <span>Best {range === "1y" ? "month" : "day"}</span>
              <strong>
                {salesStats.best && salesStats.best[salesMetric] > 0
                  ? `${salesStats.best.label} · ${salesFormatter(salesStats.best[salesMetric])}`
                  : "—"}
              </strong>
            </div>
            <div>
              <span>{range === "1y" ? "Months" : "Days"} with sales</span>
              <strong>
                {salesStats.activeDays} / {salesSeries.length}
              </strong>
            </div>
          </div>

          <div className="adb-chart adb-chart-lg">
            {salesSeries.some((row) => row.orders > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                {salesMetric === "orders" ? (
                  <BarChart data={salesSeries} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke={palette.border} strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={axisTick} tickLine={false} axisLine={false} minTickGap={18} />
                    <YAxis tick={axisTick} tickLine={false} axisLine={false} allowDecimals={false} width={36} />
                    <Tooltip
                      cursor={{ fill: palette.border, opacity: 0.35 }}
                      content={
                        <ChartTooltip
                          formatters={{ orders: (value) => `${value} orders` }}
                          labelFormatter={(_, payload) => payload?.[0]?.payload?.fullLabel}
                        />
                      }
                    />
                    <Bar
                      dataKey="orders"
                      name="Paid orders"
                      fill="#3b82f6"
                      radius={[6, 6, 0, 0]}
                      maxBarSize={28}
                      isAnimationActive={!reduceMotion}
                    />
                    {salesSeries.length > 31 && (
                      <Brush dataKey="label" height={22} stroke={palette.accent} fill={palette.card} travellerWidth={8} />
                    )}
                  </BarChart>
                ) : (
                  <AreaChart data={salesSeries} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="adb-sales-fill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={palette.accent} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={palette.accent} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke={palette.border} strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={axisTick} tickLine={false} axisLine={false} minTickGap={18} />
                    <YAxis
                      tick={axisTick}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={formatCompactMoney}
                      width={56}
                    />
                    <Tooltip
                      cursor={{ stroke: palette.accent, strokeDasharray: "4 4" }}
                      content={
                        <ChartTooltip
                          formatters={{ revenue: formatMoney, aov: formatMoney }}
                          labelFormatter={(_, payload) => {
                            const row = payload?.[0]?.payload;
                            return row ? `${row.fullLabel} · ${row.orders} order${row.orders === 1 ? "" : "s"}` : "";
                          }}
                        />
                      }
                    />
                    <Area
                      type="monotone"
                      dataKey={salesMetric}
                      name={salesMetricLabel}
                      stroke={palette.accent}
                      strokeWidth={2.5}
                      fill="url(#adb-sales-fill)"
                      activeDot={{ r: 5, strokeWidth: 2, stroke: palette.card }}
                      isAnimationActive={!reduceMotion}
                    />
                    {salesSeries.length > 31 && (
                      <Brush dataKey="label" height={22} stroke={palette.accent} fill={palette.card} travellerWidth={8} />
                    )}
                  </AreaChart>
                )}
              </ResponsiveContainer>
            ) : (
              <EmptyChart icon={<FiTrendingUp />} text="No paid sales in this period yet." />
            )}
          </div>
        </Panel>

        {/* ---------- Order status donut ---------- */}
        <Panel
          className="adb-span-4"
          title="Order status"
          subtitle="Orders placed in this period"
          icon={<FiPackage />}
          onOpen={() => navigate("/orders")}
          openLabel="Orders"
        >
          {statusTotal > 0 ? (
            <>
              <div className="adb-chart adb-donut">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius="68%"
                      outerRadius="92%"
                      paddingAngle={2}
                      stroke={palette.card}
                      strokeWidth={2}
                      isAnimationActive={!reduceMotion}
                      onMouseEnter={(_, index) => setActiveStatus(index)}
                      onMouseLeave={() => setActiveStatus(null)}
                      onClick={() => navigate("/orders")}
                    >
                      {statusData.map((row, index) => (
                        <Cell
                          key={row.status}
                          fill={row.color}
                          opacity={activeStatus === null || activeStatus === index ? 1 : 0.3}
                          style={{ cursor: "pointer", transition: "opacity .2s" }}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>

                <div className="adb-donut-center">
                  <strong>
                    {activeStatus !== null ? statusData[activeStatus]?.value : statusTotal}
                  </strong>
                  <span>{activeStatus !== null ? statusData[activeStatus]?.name : "Total orders"}</span>
                </div>
              </div>

              <ul className="adb-legend">
                {statusData.map((row, index) => (
                  <li
                    key={row.status}
                    className={activeStatus === index ? "is-active" : ""}
                    onMouseEnter={() => setActiveStatus(index)}
                    onMouseLeave={() => setActiveStatus(null)}
                  >
                    <i style={{ background: row.color }} />
                    <span>{row.name}</span>
                    <b>{row.value}</b>
                    <small>{Math.round((row.value / statusTotal) * 100)}%</small>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <EmptyChart icon={<FiPackage />} text="No orders in this period." />
          )}
        </Panel>

        {/* ---------- Top products ---------- */}
        <Panel
          className="adb-span-7"
          title="Top products"
          subtitle="Click a bar to open the product"
          icon={<FiShoppingBag />}
          actions={
            <Segmented
              label="Product metric"
              options={[
                { value: "revenue", label: "Revenue" },
                { value: "quantity", label: "Units" },
              ]}
              value={productMetric}
              onChange={setProductMetric}
            />
          }
          onOpen={() => navigate("/products")}
          openLabel="Products"
        >
          {sortedProducts.length > 0 ? (
            <div className="adb-chart" style={{ height: Math.max(sortedProducts.length * 44 + 20, 180) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={sortedProducts}
                  layout="vertical"
                  margin={{ top: 0, right: 24, left: 0, bottom: 0 }}
                  barCategoryGap={10}
                >
                  <CartesianGrid stroke={palette.border} strokeDasharray="3 3" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={axisTick}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={productMetric === "revenue" ? formatCompactMoney : undefined}
                    allowDecimals={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={150}
                    tick={{ ...axisTick, fill: palette.text }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(name) => (name.length > 20 ? `${name.slice(0, 19)}…` : name)}
                  />
                  <Tooltip
                    cursor={{ fill: palette.border, opacity: 0.3 }}
                    content={
                      <ChartTooltip
                        formatters={{
                          revenue: formatMoney,
                          quantity: (value) => `${value} units`,
                        }}
                        labelFormatter={(_, payload) => {
                          const row = payload?.[0]?.payload;
                          return row ? `${row.name} · ${row.orders} order${row.orders === 1 ? "" : "s"}` : "";
                        }}
                      />
                    }
                  />
                  <Bar
                    dataKey={productMetric}
                    name={productMetric === "revenue" ? "Revenue" : "Units sold"}
                    radius={[0, 6, 6, 0]}
                    maxBarSize={26}
                    isAnimationActive={!reduceMotion}
                    onClick={(entry) => {
                      const id = entry?.id ?? entry?.payload?.id;
                      if (id) navigate(`/products/edit/${id}`);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    {sortedProducts.map((row, index) => (
                      <Cell
                        key={row.id}
                        fill={palette.accent}
                        fillOpacity={1 - index * 0.09}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyChart icon={<FiShoppingBag />} text="No product sales in this period." />
          )}
        </Panel>

        {/* ---------- Funnel ---------- */}
        <Panel
          className="adb-span-5"
          title="Conversion funnel"
          subtitle="From cart to paid order"
          icon={<FiTarget />}
        >
          <ol className="adb-funnel">
            {funnelSteps.map((step, index) => {
              const width = Math.max((step.value / funnelMax) * 100, step.value > 0 ? 6 : 2);
              const previous = funnelSteps[index - 1];
              const stepRate =
                previous && previous.value > 0 ? (step.value / previous.value) * 100 : null;

              return (
                <li key={step.label}>
                  {stepRate !== null && (
                    <span className="adb-funnel-rate">
                      <FiArrowDownRight /> {formatPercent(stepRate)} continue
                    </span>
                  )}
                  <div className="adb-funnel-row">
                    <span className="adb-funnel-icon">{step.icon}</span>
                    <div className="adb-funnel-body">
                      <div className="adb-funnel-label">
                        <span>{step.label}</span>
                        <strong>
                          <CountUp value={step.value} />
                        </strong>
                      </div>
                      <div className="adb-funnel-track">
                        <span style={{ width: `${width}%`, opacity: 1 - index * 0.2 }} />
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>

          <div className="adb-callout">
            <FiShoppingCart />
            <div>
              <strong>{formatMoney(cartInsights.active_cart_value)}</strong> sitting in{" "}
              {cartInsights.active_carts || 0} active carts
              <small>{cartInsights.abandoned_carts || 0} idle for over 30 minutes</small>
            </div>
          </div>
        </Panel>

        {/* ---------- Payment mix ---------- */}
        <Panel
          className="adb-span-4"
          title="Payment mix"
          subtitle="Successful and refunded payments"
          icon={<FiCreditCard />}
        >
          {paymentTotal > 0 ? (
            <div className="adb-split">
              <div className="adb-chart adb-donut adb-donut-sm">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={paymentData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius="62%"
                      outerRadius="92%"
                      paddingAngle={2}
                      stroke={palette.card}
                      strokeWidth={2}
                      isAnimationActive={!reduceMotion}
                      onMouseEnter={(_, index) => setActivePayment(index)}
                      onMouseLeave={() => setActivePayment(null)}
                    >
                      {paymentData.map((row, index) => (
                        <Cell
                          key={row.name}
                          fill={row.color}
                          opacity={activePayment === null || activePayment === index ? 1 : 0.3}
                          style={{ transition: "opacity .2s" }}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="adb-donut-center">
                  <strong>
                    {activePayment !== null
                      ? `${Math.round((paymentData[activePayment].value / paymentTotal) * 100)}%`
                      : formatCompactMoney(paymentTotal)}
                  </strong>
                  <span>{activePayment !== null ? paymentData[activePayment].name : "Collected"}</span>
                </div>
              </div>

              <ul className="adb-legend is-compact">
                {paymentData.map((row, index) => (
                  <li
                    key={row.name}
                    className={activePayment === index ? "is-active" : ""}
                    onMouseEnter={() => setActivePayment(index)}
                    onMouseLeave={() => setActivePayment(null)}
                  >
                    <i style={{ background: row.color }} />
                    <span>
                      {row.name}
                      <small>{row.count} payments</small>
                    </span>
                    <b>{formatCompactMoney(row.value)}</b>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <EmptyChart icon={<FiCreditCard />} text="No payments in this period." />
          )}
        </Panel>

        {/* ---------- Top markets ---------- */}
        <Panel
          className="adb-span-4"
          title="Top markets"
          subtitle="Paid sales by destination"
          icon={<FiMapPin />}
        >
          {locations.length > 0 ? (
            <ul className="adb-bars">
              {locations.map((location, index) => {
                const revenue = Number(location.revenue || 0);
                return (
                  <li key={location.location} title={`${location.orders} orders · ${formatMoney(revenue)}`}>
                    <div className="adb-bars-label">
                      <span>
                        <em>{index + 1}</em>
                        {location.location}
                      </span>
                      <b>{formatCompactMoney(revenue)}</b>
                    </div>
                    <div className="adb-bars-track">
                      <span
                        style={{
                          width: `${Math.max((revenue / maxLocationRevenue) * 100, 4)}%`,
                          background: index === 0 ? palette.accent : SERIES_COLORS[(index - 1) % SERIES_COLORS.length],
                        }}
                      />
                    </div>
                    <small>{location.orders} orders</small>
                  </li>
                );
              })}
            </ul>
          ) : (
            <EmptyChart icon={<FiMapPin />} text="No location data yet." />
          )}
        </Panel>

        {/* ---------- Customer health ---------- */}
        <Panel
          className="adb-span-4"
          title="Customer loyalty"
          subtitle="Repeat buyers & delivery speed"
          icon={<FiRepeat />}
          onOpen={() => navigate("/customers")}
          openLabel="Customers"
        >
          <div className="adb-split">
            <div className="adb-chart adb-donut adb-donut-sm">
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart
                  innerRadius="72%"
                  outerRadius="100%"
                  data={[{ name: "Repeat rate", value: Math.min(repeatRate, 100), fill: "#10b981" }]}
                  startAngle={90}
                  endAngle={-270}
                >
                  <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                  <RadialBar
                    dataKey="value"
                    cornerRadius={10}
                    background={{ fill: palette.border }}
                    isAnimationActive={!reduceMotion}
                  />
                </RadialBarChart>
              </ResponsiveContainer>
              <div className="adb-donut-center">
                <strong>{formatPercent(repeatRate)}</strong>
                <span>Repeat rate</span>
              </div>
            </div>

            <dl className="adb-metrics">
              <div>
                <dt>Total customers</dt>
                <dd><CountUp value={userStats.total_customers} /></dd>
              </div>
              <div>
                <dt>New in 30 days</dt>
                <dd><CountUp value={userStats.new_customers_30d} /></dd>
              </div>
              <div>
                <dt>Buyers this period</dt>
                <dd>
                  {customers.purchasing_customers || 0}
                  <small> · {customers.repeat_customers || 0} repeat</small>
                </dd>
              </div>
              <div>
                <dt>Avg. delivery time</dt>
                <dd>{Number(fulfilment.average_days || 0).toFixed(1)} days</dd>
              </div>
            </dl>
          </div>
        </Panel>

        {/* ---------- Needs attention ---------- */}
        <div className="adb-span-12">
          <div className="adb-section-title">
            <FiAlertTriangle />
            <h2>Needs your attention</h2>
          </div>

          <div className="adb-attention">
            {[
              {
                key: "pending",
                icon: <FiClock />,
                label: "Pending orders",
                value: summary.pending_orders || 0,
                hint: "Waiting to be confirmed",
                to: "/orders",
              },
              {
                key: "carts",
                icon: <FiShoppingCart />,
                label: "Abandoned carts",
                value: cartInsights.abandoned_carts || 0,
                hint: `${formatMoney(cartInsights.active_cart_value)} in open carts`,
              },
              {
                key: "disputes",
                icon: <FiAlertTriangle />,
                label: "Open disputes",
                value: disputes.active || 0,
                hint: `${disputes.urgent || 0} urgent · ${disputes.awaiting_admin || 0} awaiting reply`,
                to: "/disputes",
                urgent: Number(disputes.urgent || 0) > 0,
              },
              {
                key: "returns",
                icon: <FiRotateCcw />,
                label: "Cancelled & refunded",
                value: Number(period.cancelled_orders || 0) + Number(period.refunded_orders || 0),
                hint: `${period.cancelled_orders || 0} cancelled · ${period.refunded_orders || 0} refunded`,
                to: "/orders",
              },
              {
                key: "invoices",
                icon: <FiFileText />,
                label: "Invoices issued",
                value: summary.total_invoices || 0,
                hint: `${formatMoney(period.discounts)} discounts given`,
                to: "/invoices",
                calm: true,
              },
            ].map((item) => {
              const Component = item.to ? "button" : "div";
              const tone = item.calm ? "calm" : item.urgent ? "urgent" : item.value > 0 ? "warn" : "ok";

              return (
                <Component
                  key={item.key}
                  type={item.to ? "button" : undefined}
                  className={`adb-alert is-${tone} ${item.to ? "is-clickable" : ""}`}
                  onClick={item.to ? () => navigate(item.to) : undefined}
                >
                  <span className="adb-alert-icon">{item.icon}</span>
                  <div>
                    <span>{item.label}</span>
                    <strong>
                      <CountUp value={item.value} />
                    </strong>
                    <small>{item.hint}</small>
                  </div>
                  {item.to && <FiChevronRight className="adb-alert-go" />}
                </Component>
              );
            })}
          </div>
        </div>

        {/* ---------- Recent activity ---------- */}
        <Panel
          className="adb-span-7"
          title="Recent orders"
          subtitle="Latest activity across the store"
          icon={<FiShoppingBag />}
          onOpen={() => navigate("/orders")}
        >
          {recentOrders.length > 0 ? (
            <div className="adb-table-wrap">
              <table className="adb-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Customer</th>
                    <th>Status</th>
                    <th className="is-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map((order) => {
                    const customer = parseJson(order.customer_snapshot);
                    const meta = getStatusMeta(order.order_status);

                    return (
                      <tr key={order.order_id} onClick={() => navigate("/orders")}>
                        <td>
                          <strong>{order.order_number}</strong>
                          <small>{timeAgo(order.createdAt)}</small>
                        </td>
                        <td>
                          <span className="adb-ellipsis">
                            {customer.name || customer.email || "Customer"}
                          </span>
                        </td>
                        <td>
                          <span className="adb-status" style={{ "--status": meta.color }}>
                            {meta.label}
                          </span>
                          {order.payment_status && order.payment_status !== "paid" && (
                            <small className="adb-pay">{order.payment_status}</small>
                          )}
                        </td>
                        <td className="is-right">
                          <b>{formatMoney(order.total_amount)}</b>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyChart icon={<FiShoppingBag />} text="No orders yet." />
          )}
        </Panel>

        <Panel
          className="adb-span-5"
          title="New customers"
          subtitle="Most recent sign-ups"
          icon={<FiUsers />}
          onOpen={() => navigate("/customers")}
        >
          {recentCustomers.length > 0 ? (
            <ul className="adb-people">
              {recentCustomers.slice(0, 6).map((customer) => (
                <li key={customer.user_id}>
                  {customer.image_url ? (
                    <img src={customer.image_url} alt="" />
                  ) : (
                    <span className="adb-avatar">
                      {String(customer.name || customer.email || "C").charAt(0).toUpperCase()}
                    </span>
                  )}
                  <div>
                    <strong>{customer.name || "Customer"}</strong>
                    <span>{customer.email}</span>
                  </div>
                  <small>{timeAgo(customer.createdAt || customer.created_at)}</small>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyChart icon={<FiUsers />} text="No customers yet." />
          )}
        </Panel>
      </div>
    </div>
  );
};

export default Dashboard;
